import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import AddBatchModal from './AddBatchModal';

interface Batch {
  batch_id: string;
  institute_id: string;
  branch_id: string;
  course_id: string;
  batch_name: string;
  batch_time: string;
  start_month: string;
  start_year: number;
  end_month: string;
  end_year: number;
  is_active: boolean;
  created_at: string | null;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  fees_slab?: {
    fees_slab_id: string;
    base_fee: number;
    minimum_fee: number;
    maximum_fee: number;
    status: string;
    created_at?: string;
  } | null;
}

interface ApiResponse {
  success: boolean;
  data: Batch[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const Batches: React.FC = () => {
  const toast = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});
  const navigate = useNavigate();

  const [batches, setBatches] = useState<Batch[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ visible: boolean; batchId: string; batchName: string }>(
    { visible: false, batchId: '', batchName: '' }
  );

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .batch-card .p-card-body { padding: 0 !important; }
      .batch-card .p-card-content { padding: 0 !important; }
      .p-menu {
        border-radius: 10px !important;
        border: 1px solid #E5E7EB !important;
        box-shadow: 0 6px 18px rgba(14,34,93,0.10) !important;
        padding: 6px !important;
      }
      .p-menu .p-menuitem-link {
        border-radius: 7px !important;
        padding: 8px 10px !important;
        margin: 2px 0 !important;
        transition: all 0.15s ease !important;
      }
      .p-menu .p-menuitem-link:hover { background: rgba(14,34,93,0.06) !important; }
      .p-menu .p-menuitem-icon { color: #0e225d !important; font-size: 0.8rem !important; }
      .p-menu .p-menuitem-text {
        color: #0e225d !important;
        font-weight: 600 !important;
        font-size: 0.8rem !important;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (profile?.institute_id && profile?.branch_id) {
      fetchCourses();
      fetchBatches();
    }
  }, [page, search, profile]);

  const fetchCourses = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Course[] }>('/v1/courses?status=active');
      if (response.data.success) setCourses(response.data.data);
    } catch (err) { console.error('Failed to fetch courses:', err); }
  };

  const fetchBatches = async () => {
    if (!profile?.institute_id || !profile?.branch_id) {
      setError('Institute or Branch information is missing.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: page.toString(), limit: '10', ...(search && { search }) });
      const response = await apiClient.get<ApiResponse>(`/v1/batches?${params}`);
      if (response.data.success) {
        setBatches(response.data.data);
        setPagination(response.data.pagination);
      } else { setError('Failed to load batches'); }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load batches');
    } finally { setLoading(false); }
  };

  const handleAddBatch = () => { setEditingBatch(null); setShowAddModal(true); };
  const handleEditBatch = (batch: Batch) => { setEditingBatch(batch); setShowAddModal(true); };
  const handleModalClose = () => { setShowAddModal(false); setEditingBatch(null); };
  const handleModalSuccess = () => {
    const wasEditing = !!editingBatch;
    toast.current?.show({
      severity: 'success', summary: 'Success',
      detail: wasEditing ? 'Batch updated successfully!' : 'Batch created successfully!', life: 3000
    });
    fetchBatches();
    setShowAddModal(false);
    setEditingBatch(null);
  };

  const handleDeleteBatch = (batchId: string, batchName: string) =>
    setDeleteDialog({ visible: true, batchId, batchName });

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/batches/${deleteDialog.batchId}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Batch deleted successfully', life: 3000 });
      fetchBatches();
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete batch', life: 4000 });
    }
  };

  const getMenuItems = (batch: Batch) => [
    { label: 'Edit', icon: 'pi pi-pencil', command: () => handleEditBatch(batch) },
    {
      label: batch.is_active ? 'Deactivate' : 'Activate',
      icon: batch.is_active ? 'pi pi-ban' : 'pi pi-check-circle',
      command: async () => {
        try {
          await apiClient.put(`/v1/batches/${batch.batch_id}`, { is_active: !batch.is_active });
          toast.current?.show({
            severity: 'success', summary: 'Success',
            detail: `Batch ${batch.is_active ? 'deactivated' : 'activated'} successfully`, life: 3000
          });
          fetchBatches();
        } catch (err: any) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to update batch status', life: 4000 });
        }
      }
    },
    { label: 'Delete', icon: 'pi pi-trash', command: () => handleDeleteBatch(batch.batch_id, batch.batch_name) }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateString; }
  };

  const getCourseName = (courseId: string) => {
    const course = courses.find(c => c.course_id === courseId);
    return course ? course.name : 'Unknown Course';
  };

  const getBatchDuration = (batch: Batch) =>
    `${batch.start_month} ${batch.start_year} — ${batch.end_month} ${batch.end_year}`;

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading profile data…</p>
      </div>
    );
  }

  if (!profile?.institute_id || !profile?.branch_id) {
    return (
      <div style={{ padding: '20px' }}>
        <Message severity="warn" text="Institute or Branch information is missing. Please contact support." style={{ fontSize: '0.88rem' }} />
      </div>
    );
  }

  if (loading && batches.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading batches…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, batchId: '', batchName: '' })}
        onConfirm={confirmDelete}
        title="Delete Batch"
        message={`Are you sure you want to delete "${deleteDialog.batchName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '14px',
        padding: '12px 16px',
        boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px',
      }}>
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #E5E7EB',
          borderRadius: '9px',
          overflow: 'hidden',
        }}>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
            placeholder="Search batches…"
            style={{
              padding: '9px 12px',
              border: 'none', outline: 'none',
              fontSize: '0.82rem', color: '#1f2937',
              minWidth: '200px', background: 'transparent',
            }}
          />
          <button
            onClick={() => setPage(1)}
            style={{
              padding: '9px 13px',
              background: '#0e225d',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          >
            <i className="pi pi-search" style={{ fontSize: '0.78rem', color: '#fbb21f' }} />
          </button>
        </div>

        {/* Add Batch button */}
        <button
          onClick={handleAddBatch}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            padding: '9px 18px',
            background: '#0e225d', border: 'none', borderRadius: '9px',
            color: 'white', fontSize: '0.82rem', fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1a3370';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#0e225d';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} />
          Add Batch
        </button>
      </div>

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <Message severity="error" text={error} style={{ fontSize: '0.88rem' }} />
      )}

      {/* ── Empty State ─────────────────────────────────────────────────── */}
      {batches.length === 0 && !loading ? (
        <div style={{
          padding: '60px 20px', textAlign: 'center',
          background: 'white', borderRadius: '14px',
          border: '2px dashed #E5E7EB',
        }}>
          <div style={{
            width: '52px', height: '52px',
            background: 'rgba(14,34,93,0.06)', borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <i className="pi pi-th-large" style={{ fontSize: '1.5rem', color: '#0e225d', opacity: 0.3 }} />
          </div>
          <h3 style={{ margin: '0 0 6px 0', color: '#0e225d', fontSize: '0.95rem', fontWeight: '700' }}>
            No batches found
          </h3>
          <p style={{ margin: '0 0 18px 0', color: '#9ca3af', fontSize: '0.83rem' }}>
            {search ? 'Try adjusting your search criteria' : 'Get started by adding your first batch'}
          </p>
          {!search && (
            <button
              onClick={handleAddBatch}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '9px 18px', background: '#fbb21f',
                border: 'none', borderRadius: '9px', color: '#0e225d',
                fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(251,178,31,0.3)', transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} />
              Add First Batch
            </button>
          )}
        </div>
      ) : (
        /* ── Batch Cards Grid ─────────────────────────────────────────── */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '14px' }}>
          {batches.map((batch) => (
            <Card
              key={batch.batch_id}
              className="batch-card"
              style={{
                borderRadius: '14px', border: '1px solid #E5E7EB', background: 'white',
                boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(14,34,93,0.12)';
                e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(14,34,93,0.06)';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              {/* Card Header */}
              <div style={{
                background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 100%)',
                padding: '14px 16px', borderRadius: '14px 14px 0 0',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: '-24px', right: '-24px',
                  width: '80px', height: '80px',
                  background: 'rgba(251,178,31,0.08)', borderRadius: '50%',
                  filter: 'blur(16px)', pointerEvents: 'none',
                }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, marginRight: '10px' }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '0.92rem', fontWeight: '700', color: 'white' }}>
                      {batch.batch_name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.4' }}>
                      {getCourseName(batch.course_id)}
                    </p>
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: '20px',
                    fontSize: '0.65rem', fontWeight: '700', flexShrink: 0, letterSpacing: '0.4px',
                    ...(batch.is_active
                      ? { background: '#fbb21f', color: '#0e225d' }
                      : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' }),
                  }}>
                    {batch.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div style={{ padding: '14px 16px' }}>

                {/* Schedule */}
                <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #f3f4f6' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Schedule
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '28px', height: '28px',
                      background: 'rgba(14,34,93,0.08)', borderRadius: '7px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <i className="pi pi-clock" style={{ color: '#0e225d', fontSize: '0.78rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '600', color: '#0e225d' }}>
                      {batch.batch_time}
                    </p>
                  </div>
                </div>

                {/* Duration */}
                <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #f3f4f6' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Duration
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '28px', height: '28px',
                      background: 'rgba(251,178,31,0.12)', borderRadius: '7px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <i className="pi pi-calendar" style={{ color: '#b8860b', fontSize: '0.78rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '600', color: '#1f2937' }}>
                      {getBatchDuration(batch)}
                    </p>
                  </div>
                </div>

                {/* Created */}
                <div style={{ marginBottom: '14px' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Created
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '28px', height: '28px',
                      background: 'rgba(22,163,74,0.08)', borderRadius: '7px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <i className="pi pi-info-circle" style={{ color: '#15803d', fontSize: '0.78rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '600', color: '#6b7280' }}>
                      {formatDate(batch.created_at)}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => navigate(`/batches/${batch.batch_id}/details`)}
                    style={{
                      flex: 1, padding: '9px',
                      background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                      border: 'none', borderRadius: '9px',
                      color: 'white', fontSize: '0.78rem', fontWeight: '700',
                      cursor: 'pointer', transition: 'all 0.15s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                      boxShadow: '0 2px 6px rgba(14,34,93,0.18)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,34,93,0.28)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(14,34,93,0.18)';
                    }}
                  >
                    <i className="pi pi-eye" style={{ fontSize: '0.78rem' }} />
                    View Details
                  </button>

                  <button
                    onClick={(e) => { e.stopPropagation(); menuRefs.current[batch.batch_id]?.toggle(e); }}
                    style={{
                      padding: '9px 12px',
                      background: '#F5F7FA', border: '1px solid #E5E7EB',
                      borderRadius: '9px', color: '#0e225d',
                      cursor: 'pointer', transition: 'all 0.15s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#E8EEF7';
                      e.currentTarget.style.borderColor = '#fbb21f';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F5F7FA';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <i className="pi pi-ellipsis-v" style={{ fontSize: '0.82rem' }} />
                  </button>

                  <Menu
                    model={getMenuItems(batch)}
                    popup
                    ref={(el) => { menuRefs.current[batch.batch_id] = el; }}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: '10px', padding: '14px 18px',
          background: 'white', borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
            Page {pagination.page} of {pagination.pages} · {pagination.total} total batches
          </span>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              style={{
                width: '32px', height: '32px',
                background: page === 1 ? '#F5F7FA' : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: page === 1 ? '1px solid #E5E7EB' : 'none',
                borderRadius: '8px',
                color: page === 1 ? '#9ca3af' : '#fbb21f',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: page === 1 ? 'none' : '0 2px 6px rgba(14,34,93,0.18)',
                transition: 'all 0.15s ease',
              }}
            >
              <i className="pi pi-chevron-left" style={{ fontSize: '0.72rem' }} />
            </button>
            <button
              disabled={page === pagination.pages}
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              style={{
                width: '32px', height: '32px',
                background: page === pagination.pages ? '#F5F7FA' : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: page === pagination.pages ? '1px solid #E5E7EB' : 'none',
                borderRadius: '8px',
                color: page === pagination.pages ? '#9ca3af' : '#fbb21f',
                cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: page === pagination.pages ? 'none' : '0 2px 6px rgba(14,34,93,0.18)',
                transition: 'all 0.15s ease',
              }}
            >
              <i className="pi pi-chevron-right" style={{ fontSize: '0.72rem' }} />
            </button>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
      <AddBatchModal
        visible={showAddModal}
        instituteId={profile?.institute_id || ''}
        branchId={profile?.branch_id || ''}
        courses={courses}
        editingBatch={editingBatch}
        onHide={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Batches;